import graphql from "babel-plugin-relay/macro";
import { Provider, useSetAtom, useAtomValue } from "jotai";
import React, { useEffect } from "react";
import { useFragment } from "react-relay";

import { Box } from "@whoosh/design-system/src/components/primitive";

import { AddOrEditSignUp_event$key } from "./__generated__/AddOrEditSignUp_event.graphql";
import {
  eventSignUpProcessDispatchAtom,
  currentEventSignUpStepAtom,
  isCurrentEventFormDirtyAtom,
} from "./atoms/eventSignUpProcess";
import { ActionButtonsEventSignUp } from "./components/ActionButtonsEventSignUp/ActionButtonsEventSignUp";
import { FormStepManager } from "./components/FormStepManager";
import { useInitializeEventSignUpProcess } from "./hooks/useInitializeEventSignUpProcess";
import {
  AddPeopleRenderFunction,
  EventSignUpProcessSteps,
  EventSignUpRow,
} from "./types";

export type AddOrEditSignUpProps = {
  signUpRow: EventSignUpRow;
  eventRef: AddOrEditSignUp_event$key;
  addPeopleRenderFunction: AddPeopleRenderFunction;
  currentMemberUserId?: string;
  onSaveCallback: (signUpRow: EventSignUpRow, notifyMembers?: boolean) => void;
  onCancelCallback: () => void;
  isOpen?: boolean;
  stepToStartOn?: EventSignUpProcessSteps;
  isStaff?: boolean;
  // Department-admin status of the staff user; gates ADMIN_ONLY charges.
  isStaffAdmin?: boolean;
  setIsDirtyFormStep?: (value: boolean) => void;
  saveIsDisabled?: boolean;
  eventRegistrationCleanup?: boolean;
  isWaitlistSignup?: boolean;
};

const AddOrEditSignUpComponent = ({
  signUpRow,
  eventRef,
  currentMemberUserId,
  addPeopleRenderFunction,
  onSaveCallback,
  onCancelCallback,
  isOpen = true,
  stepToStartOn,
  isStaff,
  isStaffAdmin,
  setIsDirtyFormStep,
  saveIsDisabled,
  eventRegistrationCleanup,
  isWaitlistSignup,
}: AddOrEditSignUpProps) => {
  /**
   * Handles setting up our data - or refreshing us if data changes.
   */
  const currentEventSignUp = useAtomValue(currentEventSignUpStepAtom);
  const isCurrentEventFormDirty = useAtomValue(isCurrentEventFormDirtyAtom);

  useEffect(() => {
    if (setIsDirtyFormStep) {
      if (currentEventSignUp === "FORM" && isCurrentEventFormDirty) {
        setIsDirtyFormStep(true);
      } else {
        setIsDirtyFormStep(false);
      }
    }
  }, [currentEventSignUp, setIsDirtyFormStep, isCurrentEventFormDirty]);

  const event = useFragment(AddOrEditSignUpFragmentString, eventRef);

  useInitializeEventSignUpProcess({
    signUpRow,
    currentMemberUserId,
    eventRef: event,
    isOpen,
    onSaveCallback,
    stepToStartOn,
    isStaff,
    isStaffAdmin,
    eventRegistrationCleanup,
    isWaitlistSignup,
  });

  const dispatch = useSetAtom(eventSignUpProcessDispatchAtom);
  return (
    <Box flex={1}>
      <FormStepManager
        eventRef={event}
        addPeopleRenderFunction={addPeopleRenderFunction}
        onCancelCallback={onCancelCallback}
      />
      <ActionButtonsEventSignUp
        eventRef={event}
        cancelCallback={() => {
          onCancelCallback();
          dispatch({ type: "clear" });
        }}
        onSaveCallback={(signUpRow, notifyMembers) => {
          setIsDirtyFormStep?.(false);
          onSaveCallback(signUpRow, notifyMembers);
          dispatch({ type: "clear" });
        }}
        signUpRow={signUpRow}
        saveIsDisabled={saveIsDisabled}
      />
    </Box>
  );
};

/**
 *
 * This export simply wraps us in a jotai provider so we can keep our atoms
 * isolated
 */
export const AddOrEditSignUp = (args: AddOrEditSignUpProps) => {
  return (
    <Provider>
      <AddOrEditSignUpComponent {...args} />
    </Provider>
  );
};
const AddOrEditSignUpFragmentString = graphql`
  fragment AddOrEditSignUp_event on Event {
    ...useInitializeEventSignUpProcess_event
    ...FormStepManager_event
    ...ActionButtonsEventSignUp_event
  }
`;
